import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';

import { CopyButton } from '../../../../components/ui/CopyButton.jsx';
import { getStatusColour } from '../utils';
import { StepIcon, StatusIcon, NoteDisplay } from '../components';
import { formatTime } from './utils.ts';
import { ActionChip } from './ActionChip.tsx';
import { ActionSection } from './ActionSection';
import { ActionedBySection } from './ActionedBySection.tsx';
import { TimelineItemSection } from './TimelineItemSection.tsx';
import { HealthCategorySection } from './HealthCategorySection.tsx';
import type { TimelineItem as TimelineItemType } from './HistoryDialog.types.ts';

type TimelineItemProps = {
  item: TimelineItemType;
  onStepClick?: () => void;
  isLoading?: boolean;
  isClickable?: boolean;
};

export const TimelineItem: React.FC<TimelineItemProps> = ({
  item,
  onStepClick,
  isLoading = false,
  isClickable = true,
}) => {
  const status = item.stepSnapshot.currentStatus?.status ?? 'NOT_STARTED';
  const stepIdentifier = item.stepSnapshot.type;

  const statusColour = getStatusColour(status);

  const handleOnClick = (e: React.MouseEvent) => {
    if (isLoading) {
      return;
    }

    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('[role="button"]') || target.closest('a')) {
      return;
    }

    if (isClickable) {
      onStepClick?.();
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        gap: 1,
        height: '100%',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        borderRadius: 1,
        p: 1,
        '&:hover': isClickable
          ? {
              '#timeline-item-icon': {
                outline: '2px solid',
                outlineColor: statusColour.main,
                outlineOffset: '2px',
              },
            }
          : {},
        ':last-child': {
          '#timeline-connector': {
            display: 'none',
          },
        },
      }}
      onClick={handleOnClick}
    >
      <Box
        sx={{
          mt: 0.3,
          width: 24,
          height: '10',
          color: 'white',
          display: 'flex',
          position: 'relative',
        }}
      >
        <Box
          id="timeline-item-icon"
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: statusColour.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 1,
            zIndex: 3,
            transition: 'all 0.1s ease-in-out',
          }}
        >
          <StepIcon step={item.stepSnapshot} height={18} width={18} sx={{ fontSize: 14 }} />
        </Box>

        <Box
          id="timeline-connector"
          sx={{
            position: 'absolute',
            left: '50%',
            top: 27,
            transform: 'translateX(-50%)',
            width: 2,
            height: 'calc(100% + 4px)',
            backgroundColor: 'divider',
            zIndex: 0,
          }}
        />
      </Box>

      <Box sx={{ flex: 1 }}>
        <Stack gap={1}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <StatusIcon status={status} stepIdentifier={stepIdentifier} fontSize="small" />
              <Typography variant="subtitle1" fontWeight="bold">
                {item.title}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" gap={1}>
              <CopyButton
                noHighlight
                iconOnLeft
                disabled={!item.actionedAt}
                color="inherit"
                title={`Copy timestamp - ${item.actionedAt?.toMillis() || 'No timestamp'}`}
                textToCopy={item.actionedAt?.toMillis().toString() || 'No timestamp'}
              >
                <Typography variant="body2">{formatTime(item.actionedAt)}</Typography>
              </CopyButton>

              <ActionChip item={item} />
            </Stack>
          </Box>

          <TimelineItemSection
            title="Health Category"
            Icon={CategoryIcon}
            flexDirection="row"
            justifyContent={'space-between'}
          >
            <HealthCategorySection item={item} />
          </TimelineItemSection>

          <TimelineItemSection title="Description" Icon={NotesRoundedIcon}>
            <Typography variant="body2">{item.description}</Typography>
          </TimelineItemSection>

          <ActionSection item={item} />

          <ActionedBySection item={item} />

          {item.action !== 'UPDATE_NOTE' && !!item.stepSnapshot.currentStatus?.note && (
            <NoteDisplay stepId={item.stepId} noteConfig={item.stepSnapshot.currentStatus?.note} />
          )}
        </Stack>
      </Box>
    </Box>
  );
};
